<template>
  <div class="sidebar-wrapper">
    <!-- Main Sidebar -->
    <div
      class="potree-sidebar"
      :class="{ 'collapsed': sidebarCollapsed }"
      :style="{ width: sidebarCollapsed ? '0px' : `${sidebarWidth}px` }"
    >
      <!-- Sidebar Content (when expanded) -->
      <div v-if="!sidebarCollapsed" class="sidebar-content">
        
        <!-- Cylinder Fitting Section (conditional) -->
        <SidebarSection
          v-if="cylinderMode.active"
          title="Cylinder Fitting"
          icon="mdi-cylinder"
          :badge="`${currentStep || 0}/2`"
          :expanded="expandedSections.includes('cylinder')"
          @toggle="(val) => toggleSectionExpanded('cylinder', val)"
        >
          <div class="cylinder-panel">
            <!-- Step 1: Circle 1 -->
            <div class="step-row">
              <div class="step-indicator" :class="getStepClass(1)">
                <v-icon x-small v-if="cylinderMode.circle1Complete">mdi-check</v-icon>
                <span v-else>1</span>
              </div>
              <div class="step-content">
                <span class="step-label">Circle 1</span>
                <span class="step-status" :class="getStepClass(1)">
                  {{ cylinderMode.circle1Complete ? 'Complete' : (currentStep === 1 ? 'Active' : 'Pending') }}
                </span>
              </div>
              <v-btn
                @click="startCircle1"
                :disabled="currentStep === 2"
                x-small
                depressed
                color="primary"
                dark
              >
                {{ getCircle1ButtonText() }}
              </v-btn>
            </div>

            <!-- Step 2: Circle 2 -->
            <div class="step-row">
              <div class="step-indicator" :class="getStepClass(2)">
                <v-icon x-small v-if="cylinderMode.circle2Complete">mdi-check</v-icon>
                <span v-else>2</span>
              </div>
              <div class="step-content">
                <span class="step-label">Circle 2</span>
                <span class="step-status" :class="getStepClass(2)">
                  {{ cylinderMode.circle2Complete ? 'Complete' : (currentStep === 2 ? 'Active' : 'Pending') }}
                </span>
              </div>
              <v-btn
                @click="startCircle2"
                :disabled="!cylinderMode.circle1Complete || currentStep === 1"
                x-small
                depressed
                color="primary"
                dark
              >
                {{ getCircle2ButtonText() }}
              </v-btn>
            </div>

            <!-- Instructions -->
            <div v-if="currentStep" class="step-instructions">
              Click 3+ points on the circle perimeter, then press Enter
            </div>

            <!-- Actions -->
            <div class="cylinder-actions">
              <v-btn x-small text @click="cancelCylinderMode">Cancel</v-btn>
              <v-btn
                x-small
                depressed
                color="success"
                :disabled="!cylinderMode.circle1Complete || !cylinderMode.circle2Complete"
                @click="finalizeCylinder"
                dark
              >
                Finalize
              </v-btn>
            </div>
          </div>
        </SidebarSection>

        <!-- Measurements Section -->
        <SidebarSection
          title="Measurements"
          icon="mdi-ruler"
          :badge="activeMeasurements.length"
          :expanded="expandedSections.includes('measurements')"
          @toggle="(val) => toggleSectionExpanded('measurements', val)"
        >
          <div class="measurements-list">
            <div
              v-for="item in activeMeasurements"
              :key="item.id"
              class="measurement-item"
              :class="{
                'selected': selectedMeasurement && selectedMeasurement.id === item.id
              }"
              @click="$emit('select-measurement', item)"
            >
              <v-icon small :color="getMeasurementColor(item.type, item.lassoData)" class="item-icon">
                {{ getMeasurementIcon(item.type, item.lassoData) }}
              </v-icon>
              <div class="item-content">
                <div class="item-name">{{ item.name }}</div>
              </div>
              <div class="item-actions">
                <!-- Lasso Toggle -->
                <v-tooltip bottom v-if="item.type === 'screen-lasso' && item.lassoData">
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      class="action-btn"
                      :class="{
                        'active': activeLassoIds.includes(item.lassoData.id),
                        'clean-lasso': item.lassoData.type === 'clean'
                      }"
                      @click.stop="toggleLassoActive(item.lassoData.id)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon x-small>
                        {{ getLassoIcon(item.lassoData, activeLassoIds.includes(item.lassoData.id)) }}
                      </v-icon>
                    </button>
                  </template>
                  <span>{{ getLassoTooltip(item.lassoData, activeLassoIds.includes(item.lassoData.id)) }}</span>
                </v-tooltip>
                <!-- Delete Button -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      class="action-btn delete"
                      @click.stop="deleteMeasurement(item)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon x-small>mdi-close</v-icon>
                    </button>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </div>
            </div>

            <!-- CylinderAxisNavigation -->
            <CylinderAxisNavigation
              v-if="selectedMeasurement && selectedMeasurement.type === 'fitted-cylinder'"
              :cylinder="selectedMeasurement"
              :viewer="viewer"
              :potree-viewer-component="potreeViewerComponent"
              :cylinder-mode-active="cylinderMode.active"
              :corrosion-thresholds="corrosionThresholds"
              :corrosion-statistics="corrosionStatistics"
              @view-navigated="$emit('cylinder-view-navigated', $event)"
              @corrosion-enabled="$emit('corrosion-enabled', $event)"
              @corrosion-disabled="$emit('corrosion-disabled')"
              @corrosion-error="$emit('corrosion-error', $event)"
            />

            <!-- Empty State -->
            <div v-if="activeMeasurements.length === 0" class="empty-state">
              <v-icon size="32" color="#606060">mdi-ruler</v-icon>
              <p class="empty-title">No measurements</p>
              <p class="empty-subtitle">Use the toolbar to create measurements</p>
            </div>
          </div>
        </SidebarSection>

        <!-- Corrosion Analysis Section (conditional) -->
        <SidebarSection
          v-if="corrosionState.enabled"
          title="Corrosion Analysis"
          icon="mdi-pipe-leak"
          :expanded="expandedSections.includes('corrosion')"
          @toggle="(val) => toggleSectionExpanded('corrosion', val)"
        >
          <CorrosionAnalysisPanel
            v-if="corrosionState.statistics"
            :statistics="corrosionState.statistics"
            :initial-thresholds="corrosionThresholds"
            :tag-id="tagId"
            :clip-volume-active="corrosionState.clipVolumeActive"
            @thresholds-changed="$emit('update-corrosion-thresholds', $event)"
            @create-inspect="$emit('create-inspection-task')"
            @update-inspect="$emit('update-inspection-task')"
            @export-report="$emit('export-corrosion-report')"
            @visibility-changed="$emit('visibility-changed', $event)"
          />
        </SidebarSection>
      </div>

      <!-- Resize Handle -->
      <div
        v-if="!sidebarCollapsed"
        class="resize-handle"
        @mousedown="startResize"
      >
        <div class="resize-grip"></div>
      </div>
    </div>

    <!-- Toggle Button -->
    <div
      class="toggle-container"
      :class="{ 'collapsed': sidebarCollapsed }"
      :style="{ left: sidebarCollapsed ? '0px' : `${sidebarWidth}px` }"
    >
      <v-tooltip right>
        <template v-slot:activator="{ on, attrs }">
          <button
            class="toggle-btn"
            @click="toggleSidebar"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon small>
              {{ sidebarCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}
            </v-icon>
          </button>
        </template>
        <span>{{ sidebarCollapsed ? 'Expand (Tab)' : 'Collapse (Tab)' }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import SidebarSection from './SidebarSection.vue';
import CylinderAxisNavigation from '../navigation/CylinderAxisNavigation.vue';
import CorrosionAnalysisPanel from '../navigation/CorrosionAnalysisPanel.vue';

export default {
  name: 'PotreeSidebar',
  components: {
    SidebarSection,
    CylinderAxisNavigation,
    CorrosionAnalysisPanel
  },
  props: {
    viewer: {
      type: Object,
      default: null
    },
    potreeViewerComponent: {
      type: Object,
      default: null
    },
    activeMeasurements: {
      type: Array,
      default: () => []
    },
    selectedMeasurement: {
      type: Object,
      default: null
    },
    activeLassoIds: {
      type: Array,
      default: () => []
    },
    cylinderMode: {
      type: Object,
      default: () => ({
        active: false,
        circle1Complete: false,
        circle2Complete: false
      })
    },
    currentStep: {
      type: Number,
      default: null
    },
    corrosionState: {
      type: Object,
      default: () => ({
        enabled: false,
        statistics: null,
        cylinderData: null
      })
    },
    corrosionThresholds: {
      type: Object,
      default: null
    },
    corrosionStatistics: {
      type: Object,
      default: null
    },
    tagId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      sidebarCollapsed: false,
      sidebarWidth: 320,
      minWidth: 260,
      maxWidth: 480,
      resizing: false,
      startX: 0,
      startWidth: 0,
      expandedSections: ['measurements', 'cylinder', 'corrosion']
    };
  },
  methods: {
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      this.$emit('sidebar-toggle', this.sidebarCollapsed);
    },
    toggleSectionExpanded(section, value) {
      if (value && !this.expandedSections.includes(section)) {
        this.expandedSections.push(section);
      } else if (!value) {
        const index = this.expandedSections.indexOf(section);
        if (index > -1) {
          this.expandedSections.splice(index, 1);
        }
      }
    },
    startResize(e) {
      this.resizing = true;
      this.startX = e.clientX;
      this.startWidth = this.sidebarWidth;
      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.stopResize);
      e.preventDefault();
    },
    handleResize(e) {
      if (!this.resizing) return;
      const delta = e.clientX - this.startX;
      const newWidth = Math.max(this.minWidth, Math.min(this.maxWidth, this.startWidth + delta));
      this.sidebarWidth = newWidth;
      this.$emit('sidebar-resize', newWidth);
    },
    stopResize() {
      this.resizing = false;
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
    },
    getMeasurementColor(type, lassoData = null) {
      // For lassos, differentiate by type
      if (type === 'screen-lasso' && lassoData) {
        return lassoData.type === 'clean' ? '#f44336' : '#66bb6a';
      }
      const colors = {
        'distance': '#4a9eff',
        'area': '#4caf50',
        'volume': '#ff9800',
        'angle': '#ab47bc',
        'point': '#ef5350',
        'fitted-cylinder': '#26c6da',
        'screen-lasso': '#66bb6a'
      };
      return colors[type] || '#909090';
    },
    getMeasurementIcon(type, lassoData = null) {
      // For lassos, differentiate by type
      if (type === 'screen-lasso' && lassoData) {
        return lassoData.type === 'clean' ? 'mdi-eraser' : 'mdi-lasso';
      }
      const icons = {
        'distance': 'mdi-ruler',
        'area': 'mdi-vector-polygon',
        'volume': 'mdi-cube-outline',
        'angle': 'mdi-angle-acute',
        'point': 'mdi-crosshairs',
        'fitted-cylinder': 'mdi-cylinder',
        'screen-lasso': 'mdi-lasso'
      };
      return icons[type] || 'mdi-circle';
    },
    getLassoIcon(lassoData, isActive) {
      if (lassoData.type === 'clean') {
        return isActive ? 'mdi-eraser' : 'mdi-eraser-variant';
      }
      return isActive ? 'mdi-eye' : 'mdi-eye-off';
    },
    getLassoTooltip(lassoData, isActive) {
      if (lassoData.type === 'clean') {
        return isActive ? 'Hide Clean Lasso' : 'Show Clean Lasso';
      }
      return isActive ? 'Hide Filter' : 'Show Filter';
    },
    getStepClass(step) {
      if (step === 1) {
        if (this.cylinderMode.circle1Complete) return 'complete';
        if (this.currentStep === 1) return 'active';
      }
      if (step === 2) {
        if (this.cylinderMode.circle2Complete) return 'complete';
        if (this.currentStep === 2) return 'active';
      }
      return 'pending';
    },
    toggleLassoActive(lassoId) {
      this.$emit('toggle-lasso', lassoId);
    },
    deleteMeasurement(item) {
      this.$emit('delete-measurement', item);
    },
    startCircle1() {
      this.$emit('start-circle-1');
    },
    startCircle2() {
      this.$emit('start-circle-2');
    },
    cancelCylinderMode() {
      this.$emit('cancel-cylinder-mode');
    },
    finalizeCylinder() {
      this.$emit('finalize-cylinder');
    },
    getCircle1ButtonText() {
      if (this.cylinderMode.circle1Complete) return 'Recreate';
      if (this.currentStep === 1) return 'Placing...';
      return 'Start';
    },
    getCircle2ButtonText() {
      if (this.cylinderMode.circle2Complete) return 'Recreate';
      if (this.currentStep === 2) return 'Placing...';
      return 'Start';
    }
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.handleResize);
    document.removeEventListener('mouseup', this.stopResize);
  }
};
</script>

<style scoped>
/* ============================================
   PROFESSIONAL CAD SIDEBAR
   Clean, functional, engineer-focused
   ============================================ */

/* Design System Variables - Unified with Measurement Toolbar */
.sidebar-wrapper {
  --bg-primary: #2b2b2b;
  --bg-secondary: #323232;
  --bg-elevated: #3a3a3a;
  --bg-hover: #404040;
  --bg-selected: #4a4a4a;

  --border-color: #4a4a4a;
  --border-subtle: #3d3d3d;

  --text-primary: #e0e0e0;
  --text-secondary: #a0a0a0;
  --text-muted: #707070;

  /* Green accent - Unified with toolbar (#03624c family) */
  --accent-primary: #03624c;
  --accent-primary-light: #a8c97a;
  --accent-primary-dark: #5a7a3a;
  --accent-blue: #4a9eff;
  --accent-green: #03624c;
  --accent-red: #f44336;
  --accent-cyan: #26c6da;

  --radius: 4px;
  --transition: 150ms ease;
}

.sidebar-wrapper {
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 12px;
  color: var(--text-primary);
}

/* Main Sidebar Panel */
.potree-sidebar {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: var(--bg-primary);
  border: 2px solid rgba(3, 98, 76, 0.4) !important;
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 0 0 1px rgba(0, 0, 0, 0.3) !important;
  display: flex;
  flex-direction: row;
  transition: width var(--transition);
  z-index: 100;
  overflow: hidden;
}

.potree-sidebar.collapsed {
  width: 0 !important;
  border-right: none;
}

/* Sidebar Content */
.sidebar-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Scrollbar - Green themed */
.sidebar-content::-webkit-scrollbar {
  width: 8px;
}

.sidebar-content::-webkit-scrollbar-track {
  background: var(--bg-secondary);
  border-radius: 4px;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background: var(--accent-primary-dark);
  border-radius: 4px;
  border: 2px solid var(--bg-secondary);
}

.sidebar-content::-webkit-scrollbar-thumb:hover {
  background: var(--accent-primary);
}

.sidebar-content::-webkit-scrollbar-thumb:active {
  background: var(--accent-primary-light);
}

/* ============================================
   MEASUREMENTS LIST
   ============================================ */
.measurements-list {
  display: flex;
  flex-direction: column;
}

.measurement-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-subtle);
  cursor: pointer;
  transition: background var(--transition);
}

.measurement-item:hover {
  background: var(--bg-hover);
}

.measurement-item.selected {
  background: var(--bg-selected);
  border-left: 2px solid var(--accent-blue);
  padding-left: 10px;
}

.item-icon {
  flex-shrink: 0;
  font-size: 18px !important;
  opacity: 0.9;
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 16px;
  line-height: 16px;
}

.item-name:empty::before {
  content: 'Unnamed';
  color: var(--text-muted);
  font-style: italic;
  font-weight: 400;
}

.item-value {
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.item-actions {
  display: flex;
  gap: 4px;
  opacity: 1;
}

/* Action Buttons */
.action-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-elevated);
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition);
}

.action-btn .v-icon {
  font-size: 16px !important;
  color: var(--text-primary) !important;
}

.action-btn:hover {
  background: var(--bg-hover);
  border-color: var(--accent-primary);
}

.action-btn:hover .v-icon {
  color: var(--accent-primary-light) !important;
}

.action-btn.active {
  background: rgba(76, 175, 80, 0.2);
  border-color: var(--accent-primary);
}

.action-btn.active .v-icon {
  color: var(--accent-primary-light) !important;
}

.action-btn.delete:hover {
  background: rgba(244, 67, 54, 0.15);
  border-color: var(--accent-red);
}

.action-btn.delete:hover .v-icon {
  color: var(--accent-red) !important;
}

/* Clean Lasso - Red styling */
.action-btn.clean-lasso.active {
  background: rgba(244, 67, 54, 0.2);
  border-color: var(--accent-red);
}

.action-btn.clean-lasso.active .v-icon {
  color: var(--accent-red) !important;
}

.action-btn.clean-lasso:hover {
  border-color: var(--accent-red);
}

.action-btn.clean-lasso:hover .v-icon {
  color: var(--accent-red) !important;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  text-align: center;
}

.empty-title {
  font-weight: 500;
  color: var(--text-secondary);
  margin: 12px 0 4px;
}

.empty-subtitle {
  font-size: 11px;
  color: var(--text-muted);
  margin: 0;
}

/* ============================================
   CYLINDER FITTING PANEL
   ============================================ */
.cylinder-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.step-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  background: var(--bg-secondary);
  border-radius: var(--radius);
}

.step-indicator {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  background: var(--bg-elevated);
  color: var(--text-muted);
  border: 1px solid var(--border-color);
}

.step-indicator.active {
  background: rgba(74, 158, 255, 0.2);
  color: var(--accent-blue);
  border-color: var(--accent-blue);
}

.step-indicator.complete {
  background: rgba(76, 175, 80, 0.2);
  color: var(--accent-green);
  border-color: var(--accent-green);
}

.step-indicator.complete .v-icon {
  color: var(--accent-green) !important;
}

.step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  font-weight: 500;
  color: var(--text-primary);
}

.step-status {
  font-size: 10px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.step-status.active {
  color: var(--accent-blue);
}

.step-status.complete {
  color: var(--accent-green);
}

.step-instructions {
  font-size: 11px;
  color: var(--text-secondary);
  padding: 8px 10px;
  background: rgba(74, 158, 255, 0.1);
  border-left: 2px solid var(--accent-blue);
  border-radius: 0 var(--radius) var(--radius) 0;
}

.cylinder-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-subtle);
  margin-top: 4px;
}

/* Fix Vuetify button text colors */
.cylinder-actions .v-btn--text {
  color: var(--text-primary) !important;
}

.cylinder-actions .v-btn--text:hover {
  background: var(--bg-hover) !important;
}

.cylinder-panel .v-btn {
  font-weight: 500 !important;
  letter-spacing: 0.3px !important;
}

.cylinder-panel .v-btn.primary {
  background-color: var(--accent-primary) !important;
}

.cylinder-panel .v-btn .v-icon {
  color: inherit !important;
}

/* ============================================
   RESIZE HANDLE
   ============================================ */
.resize-handle {
  width: 6px;
  height: 100%;
  cursor: ew-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.resize-handle:hover .resize-grip {
  background: var(--text-muted);
}

.resize-grip {
  width: 2px;
  height: 32px;
  background: var(--border-color);
  border-radius: 1px;
  transition: background var(--transition);
}

/* ============================================
   TOGGLE BUTTON
   ============================================ */
.toggle-container {
  position: absolute;
  top: 12px;
  z-index: 101;
  transition: left var(--transition);
}

.toggle-btn {
  width: 28px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-elevated);
  border: 2px solid rgba(3, 98, 76, 0.4) !important;
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 0 0 1px rgba(0, 0, 0, 0.3) !important;
  border-left: none !important;
  border-radius: 0 var(--radius) var(--radius) 0;
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition);
}

.toggle-btn .v-icon {
  font-size: 18px !important;
  color: var(--text-primary) !important;
}

.toggle-container.collapsed .toggle-btn {
  border-left: 1px solid var(--border-color);
  border-radius: var(--radius);
}

.toggle-btn:hover {
  background: var(--bg-hover);
  border-color: var(--accent-primary);
}

.toggle-btn:hover .v-icon {
  color: var(--accent-primary-light) !important;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 768px) {
  .potree-sidebar {
    position: fixed;
    z-index: 1000;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.3);
  }
}

/* ============================================
   VUETIFY OVERRIDES FOR DARK THEME
   ============================================ */

/* Fix all text buttons to have visible text */
.sidebar-wrapper .v-btn--text,
.sidebar-wrapper .v-btn--outlined {
  color: var(--text-primary) !important;
}

.sidebar-wrapper .v-btn--text .v-btn__content,
.sidebar-wrapper .v-btn--outlined .v-btn__content {
  color: var(--text-primary) !important;
}

/* Ensure all icons in buttons are visible */
.sidebar-wrapper .v-btn .v-icon {
  color: inherit !important;
}

/* Fix step indicators */
.step-indicator .v-icon {
  color: inherit !important;
}
</style>
